import PageHero from "@/components/PageHero";
import { GraduationCap, Stethoscope, HeartHandshake, BookOpen, Award, Users, Building, ShieldCheck } from "lucide-react";

const PORTRAIT = "https://customer-assets.emergentagent.com/job_doctor-leader-delhi/artifacts/l564kqho_WhatsApp%20Image%202026-05-04%20at%2011.48.18%20PM.jpeg";

const ACADEMICS = [
  { k: "3", l: "Books" },
  { k: "3", l: "Research Papers" },
  { k: "7", l: "Articles" },
  { k: "1", l: "Report" },
  { k: "1", l: "Dissertation" },
  { k: "1", l: "Book Chapter" },
  { k: "1", l: "Booklet" },
];

const ADVISOR_ROLES = [
  "RDA UCMS & GTB Hospital",
  "RDA LHMC",
  "AMBULA",
  "HORIZON — Medical | IIT-JEE | Foundations",
];

const NMO_ROLES = [
  "Akhil Bhartiya Sahitya Vikas Pramukh",
  "Sampark Pramukh, Delhi",
  "Former: Treasurer & Joint Secretary",
  "Former: Student Representative (State & National)",
];

const PROFESSIONAL_BODIES = [
  "IMA East Delhi Branch (2024, 2026)",
  "IAPSM North Zone (2025)",
  "IAPSM Delhi (2026)",
  "Dadhichi Deh Dan Samiti",
];

const UCMS_LEADERSHIP = [
  "Joint Secretary — Alumni Association",
  "Former Batch Representative & Executive Member",
  "Anti-Ragging Committee",
  "Gender Championship Club",
  "Medical Education Unit Bulletin",
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="Know Your Candidate"
        title="Dr. Abhishek Garg — MBBS (UCMS) | MD (LHMC)"
        subtitle="Senior Resident, UCMS · Primary Care Physician & Public Health Specialist · Founder, Team S.A.N.K.A.L.P. A young, ethical, accessible, committed voice for Delhi's doctors."
      />

      <section className="section-y">
        <div className="container-x grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] overflow-hidden border border-[hsl(var(--border-c))]">
              <img src={PORTRAIT} alt="Dr. Abhishek Garg" className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div className="mt-6 grid grid-cols-3 text-center">
              {[
                { k: "17+", l: "Publications" },
                { k: "26", l: "Awards" },
                { k: "5+", l: "Founder Roles" },
              ].map((s) => (
                <div key={s.l} className="border border-[hsl(var(--border-c))] py-5">
                  <div className="font-display text-3xl text-[hsl(var(--accent))]">{s.k}</div>
                  <div className="text-xs uppercase tracking-widest mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7 space-y-10">
            <div>
              <div className="label-eyebrow mb-3">Profile</div>
              <h2 className="font-display text-4xl text-[hsl(var(--primary))]">A young leader. A trusted voice.</h2>
              <p className="mt-4 text-[hsl(var(--fg-muted))] leading-relaxed text-lg">
                Dr. Abhishek Garg is a Senior Resident at <span className="text-[hsl(var(--primary))] font-semibold">University College of Medical Sciences (UCMS)</span>,
                a Primary Care Physician, and a Public Health Specialist trained at <span className="text-[hsl(var(--primary))] font-semibold">Lady Hardinge Medical College (LHMC)</span>.
                He is the founder of <span className="text-[hsl(var(--primary))] font-semibold">Team S.A.N.K.A.L.P</span> — a Sewa-oriented Alliance for
                Nation-building, Knowledge, Accountability, Leadership & Policy — and contests the
                Delhi Medical Council Executive Member election with a single promise:
                <em> Strong representation. Transparent governance. A better future for doctors. </em>
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              {[
                { icon: GraduationCap, t: "Education", d: "MBBS (UCMS) · MD (LHMC). Continuing engagement in public health policy." },
                { icon: Stethoscope, t: "Clinical Role", d: "Senior Resident, UCMS — Primary Care Physician & Public Health Specialist." },
                { icon: HeartHandshake, t: "Founder & Sankalp", d: "Founder of Team S.A.N.K.A.L.P, F.A.M.E, WeVaku Foundation, Project Gyan Sindhu, Organum Donum." },
                { icon: Award, t: "Recognition", d: "26 awards — 4 Local · 1 District · 8 State · 7 National · 6 Institutional." },
              ].map((b) => (
                <div key={b.t} className="card-flat" data-testid={`about-card-${b.t}`}>
                  <b.icon className="text-[hsl(var(--accent))]" size={28} />
                  <div className="font-display text-xl mt-3 text-[hsl(var(--primary))]">{b.t}</div>
                  <p className="text-sm text-[hsl(var(--fg-muted))] mt-2 leading-relaxed">{b.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ACADEMIC CONTRIBUTIONS */}
      <section className="bg-[hsl(var(--bg-muted))] section-y">
        <div className="container-x">
          <div className="flex items-center gap-3 mb-3">
            <BookOpen className="text-[hsl(var(--accent))]" />
            <div className="label-eyebrow">Academic Contributions</div>
          </div>
          <h2 className="font-display text-4xl text-[hsl(var(--primary))]">17+ publications across formats</h2>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7">
            {ACADEMICS.map((a) => (
              <div key={a.l} className="bg-white border border-[hsl(var(--border-c))] py-7 text-center">
                <div className="font-display text-4xl text-[hsl(var(--accent))]">{a.k}</div>
                <div className="text-[10px] uppercase tracking-widest mt-1 text-[hsl(var(--fg-muted))]">{a.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LEADERSHIP POSITIONS */}
      <section className="section-y">
        <div className="container-x">
          <div className="label-eyebrow mb-3">Leadership Positions</div>
          <h2 className="font-display text-4xl text-[hsl(var(--primary))]">A track record across institutions</h2>

          <div className="mt-12 grid md:grid-cols-3 gap-6">
            <div className="card-flat" data-testid="leadership-nmo">
              <Users className="text-[hsl(var(--accent))]" />
              <h3 className="font-display text-xl mt-4 text-[hsl(var(--primary))]">National Medicos Organisation (NMO)</h3>
              <ul className="mt-3 space-y-2 text-sm list-disc pl-5 text-[hsl(var(--fg-muted))]">
                {NMO_ROLES.map((r) => <li key={r}>{r}</li>)}
              </ul>
            </div>
            <div className="card-flat" data-testid="leadership-prof">
              <Building className="text-[hsl(var(--accent))]" />
              <h3 className="font-display text-xl mt-4 text-[hsl(var(--primary))]">Professional Bodies</h3>
              <ul className="mt-3 space-y-2 text-sm list-disc pl-5 text-[hsl(var(--fg-muted))]">
                {PROFESSIONAL_BODIES.map((r) => <li key={r}>{r}</li>)}
              </ul>
            </div>
            <div className="card-flat" data-testid="leadership-ucms">
              <ShieldCheck className="text-[hsl(var(--accent))]" />
              <h3 className="font-display text-xl mt-4 text-[hsl(var(--primary))]">UCMS Leadership</h3>
              <ul className="mt-3 space-y-2 text-sm list-disc pl-5 text-[hsl(var(--fg-muted))]">
                {UCMS_LEADERSHIP.map((r) => <li key={r}>{r}</li>)}
              </ul>
            </div>
          </div>

          {/* Advisor roles */}
          <div className="mt-12 bg-[hsl(var(--primary))] text-white p-8 sm:p-10">
            <div className="label-eyebrow text-white/80 mb-3">Advisor Roles</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {ADVISOR_ROLES.map((r) => (
                <div key={r} className="border border-white/20 p-4 text-sm" data-testid={`advisor-${r}`}>
                  {r}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
